import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { NextButton } from '@components/SiteUserUpload/Components/NextButton';
import { Select } from '@dynamika-shared/ui/Select/Select';
import {
  VisitDefinitionsStatusText,
  VisitDropdown,
} from '@components/SiteUserUpload/types/uploadTypes';
import { CheckIcon, Warning } from '@dynamika-shared/ui/icons';
import { twMerge } from '@utils/twMerge';
import {
  CreateVisit,
  ExistingVisit,
  UploadVisitType,
  VisitProps,
} from 'src/store-redux/siteUpload/siteUploadStore.types';
import { VisitConfig } from '@dynamika-shared/types/visits';
import './VisitStep.scss';

import { Divider, Radio, Tooltip } from 'antd';
import { TruncatedText } from '@dynamika-shared/ui/TruncatedText/TruncatedText';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { useVisitStepHook } from '@components/SiteUserUpload/Components/Steps/hooks/useVisitStep.hook';
import { Modal } from '@dynamika-shared/ui/Modal/Modal';
import {
  formatUnscheduledVisitName,
  parseUnscheduledVisitName,
} from '@components/SiteUserUpload/helpers/visitHelpers';
import { useSiteUploadSlice } from '@restore/siteUpload/siteUpload.slice';
import { useClinicalTrialContextSlice } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import { ExpandedData } from '@restore/clinicalTrialContext/clinicalTrialContext.types';
import { SiteSubjectType } from '@dynamika-shared/types/subjects';
import { getLabelsForVisit } from '@utils/getLabelsForVisit';
import { uploadFileNamesMap } from '@dynamika-shared/types/inventory';
import { fileTypeConfig } from './utils/detectFileExtension';

const textByVisitType: Record<
  UploadVisitType.EARLY_TERMINATED | UploadVisitType.UNSCHEDULED,
  { title: string; text: string; inputText: string; visitName: string }
> = {
  [UploadVisitType.UNSCHEDULED]: {
    title: 'Unscheduled Visit',
    text: 'Are you sure you want to create Unscheduled visit. If you do not complete the data upload, visit record will be removed.',
    inputText: 'New Unscheduled Visit',
    visitName: 'Unscheduled',
  },
  [UploadVisitType.EARLY_TERMINATED]: {
    title: 'Early Termination Visit',
    text: 'You are about to add Early Termination visit, which is going to be treated as a last subject visit in the clinical trial.',
    inputText: 'New Early Termination Visit',
    visitName: 'Early termination',
  },
};

export interface VisitStepProps {
  setCurrentStep: Dispatch<SetStateAction<number>>;
  currentStep: number;
  data: ExpandedData | null;
  subject: SiteSubjectType | null | undefined;
  setDirtyDrawerState: Dispatch<SetStateAction<boolean>>;
  setVisitName: (visitName: string) => void;
}

export const VisitStep: FC<VisitStepProps> = ({
  setCurrentStep,
  currentStep,
  subject,
  setDirtyDrawerState,
  setVisitName,
  data,
}) => {
  const {
    clinicalTrialId,
    siteId,
    createVisit,
    selectedVisit,
    setSelectedVisit,
    flatData,
    modalOpen,
    setModalOpen,
    cohort,
    subjectDetails,
  } = useVisitStepHook({ setDirtyDrawerState, data, subject });

  const ctContext = useClinicalTrialContextSlice(state => state.data);
  const isNewUnscheduled =
    selectedVisit?.id === UploadVisitType.EARLY_TERMINATED ||
    selectedVisit?.id === UploadVisitType.UNSCHEDULED;
  const isEarlyTerminated =
    selectedVisit?.id === UploadVisitType.EARLY_TERMINATED ||
    (selectedVisit as ExistingVisit)?.earlyTerminated ||
    (selectedVisit as ExistingVisit)?.type === UploadVisitType.EARLY_TERMINATED;
  const isUnscheduled =
    selectedVisit?.id === UploadVisitType.UNSCHEDULED ||
    (selectedVisit as ExistingVisit)?.type === UploadVisitType.UNSCHEDULED ||
    isEarlyTerminated;

  const allLabels = ctContext?.configuration?.labelGroups;

  const visitLabels = getLabelsForVisit(
    ctContext,
    selectedVisit?.visitDefinitionId
  );

  const supportedFormats = useMemo(() => {
    return isUnscheduled
      ? fileTypeConfig(allLabels?.flatMap(group => group.labels))
      : fileTypeConfig(visitLabels);
  }, [allLabels, isUnscheduled, visitLabels]);

  const [fileType, setFileType] = useSiteUploadSlice((state, actions) => [
    state.fileType,
    actions.setFileType,
  ]);

  useEffect(() => {
    if (fileType && !supportedFormats.includes(fileType)) {
      setFileType(null);
    }
  }, [fileType, supportedFormats]);

  useEffect(() => {
    setFileType(null);
  }, []);

  const handleSelectChange = (
    visit: SetStateAction<VisitProps | undefined>
  ) => {
    setSelectedVisit(visit);
  };

  useEffect(() => {
    selectedVisit?.visitName && setVisitName(selectedVisit.visitName);
  }, [selectedVisit?.visitName, setVisitName]);

  const visits = useMemo(() => {
    return (
      subjectDetails?.visits?.length ? [...subjectDetails.visits] : []
    ).reduce<{
      scheduled: VisitDropdown[];
      unscheduled: VisitDropdown[];
    }>(
      (acc, visit) => {
        const isUnscheduled =
          visit.type === 'UNSCHEDULED' && !visit.earlyTerminated;
        const type = visit.type !== 'UNSCHEDULED' ? 'scheduled' : 'unscheduled';
        const closed = visit.status === 'CLOSED';

        const visitFromConfig: VisitConfig | undefined = subject?.cohortId
          ? flatData?.[subject.cohortId]?.visits?.find(
              matchedVisit => matchedVisit.id === visit.visitDefinitionId
            )
          : undefined;

        const statusText =
          VisitDefinitionsStatusText[
            visit.status as keyof typeof VisitDefinitionsStatusText
          ] || visit.status;

        const unscheduledVisitName = formatUnscheduledVisitName(visit.visitName);

        const visitName = isUnscheduled
          ? {
              name: `Unscheduled ${unscheduledVisitName}`,
              label: (
                <span className='flex gap-1'>
                  <span>Unscheduled</span>
                  <span className='text-dimGray'>•</span>
                  <span className='text-spanishGray'>{unscheduledVisitName}</span>
                </span>
              ),
            }
          : {
              name: visit.visitName,
              label: <span>{visit.visitName}</span>,
            };
        const visitOption: VisitDropdown = {
          ...visit,
          value: visit.id ?? visit.visitDefinitionId,
          disabled: closed,
          label: (
            <div className='flex h-[44px] justify-between'>
              <Tooltip
                color='white'
                title={<div className='text-black'> {visitName.name}</div>}
              >
                <p
                  className={twMerge(
                    'truncate text-1624 leading-[44px] pr-1 font-medium flex items-center',
                    closed && 'text-spanishGray'
                  )}
                >
                  {visitName.label}
                </p>
              </Tooltip>
              {statusText !== VisitDefinitionsStatusText.CREATED && (
                <p className='flex items-center text-1216 text-spanishGray'>
                  {closed && <CheckIcon className='mr-0.5 h-1.5 w-1.5' />}{' '}
                  {statusText}
                </p>
              )}
            </div>
          ),
          desc: (
            <TruncatedText
              text={visitName.name}
              truncate
              tooltip
              className={twMerge(
                'text-1624 font-medium',
                closed && 'text-spanishGray'
              )}
            />
          ),
          baseline: visitFromConfig?.daysFromBaseline ?? null,
          closed,
        };

        return {
          ...acc,
          [type]: [...acc[type], visitOption],
        };
      },
      {
        scheduled: [],
        unscheduled: [],
      }
    );
  }, [flatData, subject, subjectDetails]);

  const hasEarlyterminatedVisit = useMemo(
    () => visits.unscheduled.some(visit => visit.earlyTerminated),
    [visits.unscheduled]
  );

  const hasSameDayUnscheduledVisit = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);

    return visits.unscheduled.some(
      visit =>
        !visit.earlyTerminated &&
        !visit.closed &&
        parseUnscheduledVisitName(visit.visitName)?.date === today
    );
  }, [visits.unscheduled]);

  const handleSubmit = async () => {
    if (selectedVisit && clinicalTrialId && siteId && subject && fileType) {
      const { id, visitDefinitionId } = selectedVisit;
      const data: CreateVisit = {
        patientId: subject.id,
        ...(!isNewUnscheduled && !visitDefinitionId && { visitId: id }),
        createUnscheduled: isNewUnscheduled,
        earlyTerminated: isEarlyTerminated,
        fileType,
        visitDefinitionId,
      };
      setCurrentStep(currentStep + 1);

      createVisit({
        data,
        selectedClinicalTrial: clinicalTrialId,
        selectedSite: siteId,
      });
    }
  };
  const [optionsClassName, setOptionsClassName] = useState('custom-option');

  const dropdownRenderer = (menu: ReactNode) => (
    <div className={optionsClassName}>
      <div
        onMouseEnter={() => setOptionsClassName('custom-option-active')}
        onMouseLeave={() => setOptionsClassName('custom-option')}
      >
        {menu}
      </div>
      <div className='border-t-[1px] mx-[12px]'>
        <div className='my-[10px]'>
          {cohort?.unscheduledAllowed && !hasSameDayUnscheduledVisit && (
            <Button
              type='text'
              className='text-vividTangelo hover:text-honoluluBlue !font-medium without-bg -ml-2'
              data-testid='newUnscheduledVisit'
              onClick={() => {
                setModalOpen(UploadVisitType.UNSCHEDULED);
              }}
            >
              + New Unscheduled Visit
            </Button>
          )}
          <Tooltip
            title={
              hasEarlyterminatedVisit &&
              'This subject already has Early Termination visit'
            }
          >
            <Button
              type='text'
              className={twMerge(
                'text-vividTangelo hover:text-honoluluBlue !font-medium without-bg -ml-2',
                hasEarlyterminatedVisit &&
                  'opacity-30 !text-black !:hover:text-black'
              )}
              onClick={() => {
                setModalOpen(UploadVisitType.EARLY_TERMINATED);
              }}
              disabled={hasEarlyterminatedVisit}
            >
              + Early Termination Visit
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );

  const sortedRegularVisits = useMemo(
    () =>
      [...visits.scheduled].sort((a, b) => {
        return Number(a?.baseline) - Number(b?.baseline);
      }),
    [visits.scheduled]
  );

  const sortedUnscheduledVisits = useMemo(
    () =>
      [...visits.unscheduled].sort((a, b) => {
        const nameA = parseUnscheduledVisitName(a.visitName);
        const nameB = parseUnscheduledVisitName(b.visitName);

        if (!nameA) {
          return nameB ? 1 : 0;
        }

        if (!nameB) {
          return -1;
        }

        if (nameA.date !== nameB.date) {
          return nameA.date.localeCompare(nameB.date);
        }

        return nameA.occurrence - nameB.occurrence;
      }),
    [visits.unscheduled]
  );

  const options = useMemo(
    () => [
      ...(visits.scheduled?.length
        ? [
            {
              label: <span>Planned</span>,
              title: 'scheduled',
              options: sortedRegularVisits,
            },
          ]
        : []),
      ...(visits.unscheduled?.length
        ? [
            {
              label: <span>Unscheduled</span>,
              title: 'unscheduled',
              options: sortedUnscheduledVisits,
            },
          ]
        : []),
    ],
    [visits, sortedRegularVisits, sortedUnscheduledVisits]
  );

  return (
    <div className='h-full flex flex-col justify-between'>
      <div className='mt-10'>
        <div className='flex items-start justify-center' id='uploadVisitStep'>
          <Select
            filterOption={(input, option) =>
              ((option?.label ?? '') as string)
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            dropdownRender={dropdownRenderer}
            name='sponsorId'
            rootClassName='[&_.ant-select-item-group]:pt-[15px] [&_.ant-select-item-group]:pb-[0]'
            className='min-h-[56px] min-w-[384px] !rounded-full text-1624 font-medium'
            placeholder={'Visit'}
            data-testid='visitId'
            options={options}
            onChange={(_, visit) => {
              handleSelectChange(
                visit as SetStateAction<VisitProps | undefined>
              );
            }}
            value={selectedVisit?.value}
            optionLabelProp='desc'
          />
          <NextButton
            onClick={handleSubmit}
            disabled={!selectedVisit || !fileType}
          />
          <Modal
            zIndex={999999}
            width={440}
            onCancel={() => {
              setFileType(null);
              setModalOpen(null);
            }}
            onOk={() => {
              handleSelectChange({
                id: modalOpen,
                value: `${modalOpen ? textByVisitType[modalOpen]?.inputText : ''}`,
                visitName: `${modalOpen ? textByVisitType[modalOpen]?.visitName : ''}`,
              } as SetStateAction<VisitProps | undefined>);
              setFileType(null);
              setModalOpen(null);
            }}
            okText='Confirm'
            centered
            cancelText='Cancel'
            maskClosable={false}
            open={!!modalOpen}
            mask={false}
            classNames={{
              header: 'text-left !mb-3',
              content: 'text-center !rounded-[2.4rem] !p-[4rem]',
              footer: 'flex justify-between !mt-3',
            }}
            title={modalOpen ? textByVisitType[modalOpen]?.title : ''}
            titleClassName='text-left'
          >
            {modalOpen ? textByVisitType[modalOpen]?.text : ''}
          </Modal>
        </div>
        <div className='my-3 mx-2'>
          <div className='mb-1'>Select file type</div>
          <Radio.Group
            name='radiogroup'
            className='flex gap-1 items-center'
            value={fileType}
            onChange={({ target }) => {
              setFileType(target.value);
            }}
          >
            {supportedFormats.map((fileType, index) => {
              return (
                <div key={fileType}>
                  <Radio className='m-0' value={fileType}>
                    {uploadFileNamesMap[fileType] ?? fileType}
                  </Radio>
                  {index < supportedFormats.length - 1 && (
                    <Divider type={'vertical'} className='pr-1' />
                  )}
                </div>
              );
            })}
          </Radio.Group>
        </div>
      </div>
      <div className='flex items-center justify-center'>
        <Warning className='mr-1 h-2 w-2 text-vividTangelo' />
        Please make sure to select a file type
      </div>
    </div>
  );
};
